import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '@/store';
import { fetchCategories } from '@/store/slices/productsSlice';
import { productsApi, shopsApi, brandsApi, categoriesApi, locationsApi, productFieldConfigApi, imagesApi, deliveryOptionApi } from '@/services/api';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { PRODUCT_CONDITIONS } from '@/utils/constants';
import {
  ArrowLeftIcon,
  PhotoIcon,
  XMarkIcon,
  TruckIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { DeliveryOption, DeliveryOptionCreate } from '@/types';
import DeliveryOptionForm from '@/components/seller/DeliveryOptionForm';

// Fix for default marker icon in Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface ProductFormData {
  name: string;
  description: string;
  price: string;
  original_price: string;
  product_type: 'brand_new' | 'recycled';
  category_id: string;
  sub_category_id: string;
  brand: string;
  model: string;
  condition: string;
  warranty: string;
  is_public: boolean;
  tags: string;
  search_keywords: string;
  country_id: string;
  state_id: string;
  city_id: string;
  district_id: string;
  area_id: string;
  latitude: string;
  longitude: string;
  // Dynamic fields will be added here as needed
  [key: string]: any;
}

interface DynamicField {
  id: number;
  field_name: string;
  field_label: string;
  field_type: string;
  is_required: boolean;
  placeholder?: string;
  help_text?: string;
  validation_rules?: any;
  options?: any[];
}

interface Category {
  id: number;
  name: string;
  description?: string;
}

// Map click handler component
const LocationPicker: React.FC<{ onLocationSelect: (lat: number, lng: number) => void }> = ({ onLocationSelect }) => {
  useMapEvents({
    click: (e) => {
      const { lat, lng } = e.latlng;
      onLocationSelect(lat, lng);
    },
  });
  return null;
};

const AddProductPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const { categories } = useSelector((state: RootState) => state.products);

  const isEditMode = !!id;
  const editingProductId = id ? parseInt(id) : null;
  
  const [formData, setFormData] = useState<ProductFormData>({
    name: '',
    description: '',
    price: '',
    original_price: '',
    product_type: 'brand_new',
    category_id: '',
    sub_category_id: '',
    brand: '',
    model: '',
    condition: 'Brand New',
    warranty: '',
    is_public: true,
    tags: '',
    search_keywords: '',
    country_id: '',
    state_id: '',
    city_id: '',
    district_id: '',
    area_id: '',
    latitude: '',
    longitude: '',
  });
  
  const [images, setImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shopId, setShopId] = useState<number | null>(null);
  const [brands, setBrands] = useState<any[]>([]);
  const [subCategories, setSubCategories] = useState<Category[]>([]);
  const [isLoadingBrands, setIsLoadingBrands] = useState(false);
  const [countries, setCountries] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [districts, setDistricts] = useState<any[]>([]);
  const [areas, setAreas] = useState<any[]>([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [showMapPicker, setShowMapPicker] = useState(true);
  const [dynamicFields, setDynamicFields] = useState<DynamicField[]>([]);
  const [isLoadingFields, setIsLoadingFields] = useState(false);
  const [isLoadingProduct, setIsLoadingProduct] = useState(false);

  // Delivery Options State
  const [deliveryOptions, setDeliveryOptions] = useState<DeliveryOption[]>([]);
  const [isDeliveryFormOpen, setIsDeliveryFormOpen] = useState(false);
  const [editingDeliveryOption, setEditingDeliveryOption] = useState<DeliveryOption | null>(null);
  const [deliveryValidationError, setDeliveryValidationError] = useState('');

  useEffect(() => {
    // Fetch categories
    dispatch(fetchCategories());
    
    // Fetch brands
    const fetchBrands = async () => {
      try {
        setIsLoadingBrands(true);
        const response = await brandsApi.getAll(true); // Get only active brands
        setBrands(response.data);
      } catch (error: any) {
        console.error('Error fetching brands:', error);
      } finally {
        setIsLoadingBrands(false);
      }
    };
    
    fetchBrands();
    
    // Fetch countries
    const fetchCountries = async () => {
      try {
        setIsLoadingLocations(true);
        const response = await locationsApi.getAll('country');
        setCountries(response.data || []);
      } catch (error: any) {
        console.error('Error fetching countries:', error);
      } finally {
        setIsLoadingLocations(false);
      }
    };
    
    fetchCountries();
    
    // Fetch user's shop
    const fetchShop = async () => {
      try {
        const response = await shopsApi.getMyShop();
        console.log('Shop response:', response.data);
        setShopId(response.data.id);
      } catch (error: any) {
        console.error('Error fetching shop:', error);
        setError('You need to create a shop before adding products');
      }
    };

    if (user) {
      fetchShop();
    }
  }, [user, dispatch]);

  // Load product data when in edit mode
  useEffect(() => {
    if (isEditMode && editingProductId) {
      const loadProductData = async () => {
        try {
          setIsLoadingProduct(true);
          const response = await productsApi.getById(editingProductId);
          const product = response.data;

          // Parse location hierarchy
          let country_id = '', state_id = '', city_id = '', district_id = '', area_id = '';
          if (product.location && product.location.hierarchy) {
            const hierarchy = product.location.hierarchy;
            // Find each level in the hierarchy
            const country = hierarchy.find((loc: any) => loc.location_type === 'country');
            const state = hierarchy.find((loc: any) => loc.location_type === 'state');
            const city = hierarchy.find((loc: any) => loc.location_type === 'city');
            const district = hierarchy.find((loc: any) => loc.location_type === 'district');
            const area = hierarchy.find((loc: any) => loc.location_type === 'area');

            country_id = country?.id?.toString() || '';
            state_id = state?.id?.toString() || '';
            city_id = city?.id?.toString() || '';
            district_id = district?.id?.toString() || '';
            area_id = area?.id?.toString() || '';
          }

          // Load category and sub-category information
          let category_id = product.category_id?.toString() || '';
          let sub_category_id = '';

          // If we have category info, check if it's a sub-category
          if (product.category) {
            if (product.category.parent_id) {
              // This is a sub-category
              sub_category_id = category_id;
              category_id = product.category.parent_id.toString();
            }
          }

          // Populate form data with existing product data
          const baseFormData = {
            name: product.name || '',
            description: product.description || '',
            price: product.price?.toString() || '',
            original_price: product.original_price?.toString() || '',
            product_type: product.product_type || 'brand_new',
            category_id,
            sub_category_id,
            brand: product.brand || '',
            model: product.model || '',
            condition: product.condition || 'Brand New',
            warranty: product.warranty || '',
            is_public: product.is_public ?? true,
            tags: Array.isArray(product.tags) ? product.tags.join(', ') : '',
            search_keywords: product.search_keywords || '',
            country_id,
            state_id,
            city_id,
            district_id,
            area_id,
            latitude: product.latitude?.toString() || '',
            longitude: product.longitude?.toString() || '',
          };

          // Add dynamic fields from product data
          const dynamicFormData: any = { ...baseFormData };
          if (dynamicFields.length > 0) {
            dynamicFields.forEach(field => {
              if (product[field.field_name] !== undefined) {
                dynamicFormData[field.field_name] = product[field.field_name];
              }
            });
          }

          setFormData(dynamicFormData);

          // Load existing images if any
          if (product.images && Array.isArray(product.images) && product.images.length > 0) {
            const existingImageUrls = product.images.map((img: any) => img.image_url);
            setImagePreviews(existingImageUrls);
            // Note: We can't set File objects for existing images, so images state stays empty
            // When user adds new images, they'll be appended to imagePreviews
          }

          // Load existing delivery options
          if (product.delivery_options && Array.isArray(product.delivery_options) && product.delivery_options.length > 0) {
            setDeliveryOptions(product.delivery_options);
          } else {
            // If no delivery options exist, start with empty array
            setDeliveryOptions([]);
          }

          // After setting form data, load sub-categories if category_id is set
          if (category_id && category_id !== '') {
            try {
              const subCategoriesResponse = await categoriesApi.getAll(parseInt(category_id));
              setSubCategories(subCategoriesResponse.data);
            } catch (error) {
              console.error('Error loading sub-categories:', error);
              setSubCategories([]);
            }
          }

          // The dynamic fields useEffect will handle loading fields when category/sub-category changes

          // Load states if country is set
          if (country_id) {
            try {
              const statesResponse = await locationsApi.getAll('state', parseInt(country_id));
              setStates(statesResponse.data || []);
            } catch (error) {
              console.error('Error loading states:', error);
            }
          }

          // Load cities if state is set
          if (state_id) {
            try {
              const citiesResponse = await locationsApi.getAll('city', parseInt(state_id));
              setCities(citiesResponse.data || []);
            } catch (error) {
              console.error('Error loading cities:', error);
            }
          }

          // Load districts if city is set
          if (city_id) {
            try {
              const districtsResponse = await locationsApi.getAll('district', parseInt(city_id));
              setDistricts(districtsResponse.data || []);
            } catch (error) {
              console.error('Error loading districts:', error);
            }
          }

          // Load areas if district is set
          if (district_id) {
            try {
              const areasResponse = await locationsApi.getAll('area', parseInt(district_id));
              setAreas(areasResponse.data || []);
            } catch (error) {
              console.error('Error loading areas:', error);
            }
          }

          // Set existing images if any
          if (product.images && product.images.length > 0) {
            const existingImages: string[] = [];
            product.images.forEach((img: any) => {
              if (img.image_url) {
                existingImages.push(img.image_url);
              }
            });
            setImagePreviews(existingImages);
          }

          // Load delivery options
          try {
            const deliveryResponse = await deliveryOptionApi.getProductDeliveryOptions(editingProductId);
            const effectiveOptions = deliveryResponse.data.effective_options || [];
            setDeliveryOptions(effectiveOptions);
          } catch (error: any) {
            console.error('Error loading delivery options:', error);
            // Don't fail the whole load if delivery options fail
            setDeliveryOptions([]);
          }

        } catch (error: any) {
          console.error('Error loading product data:', error);
          setError('Failed to load product data');
          toast.error('Failed to load product data');
        } finally {
          setIsLoadingProduct(false);
        }
      };

      loadProductData();
    }
  }, [isEditMode, editingProductId]);

  // Fetch sub-categories when category changes
  useEffect(() => {
    const fetchSubCategories = async () => {
      if (formData.category_id) {
        try {
          const response = await categoriesApi.getAll(parseInt(formData.category_id));
          setSubCategories(response.data);
          // Reset sub_category_id when category changes
          setFormData(prev => ({ ...prev, sub_category_id: '' }));
        } catch (error: any) {
          console.error('Error fetching sub-categories:', error);
          setSubCategories([]);
        }
      } else {
        setSubCategories([]);
        setFormData(prev => ({ ...prev, sub_category_id: '' }));
      }
    };
    
    fetchSubCategories();
  }, [formData.category_id]);

  // Fetch dynamic fields when category or sub-category changes
  useEffect(() => {
    const fetchDynamicFields = async () => {
      // Use sub_category_id if selected, otherwise use category_id
      const finalCategoryId = formData.sub_category_id || formData.category_id;

      if (finalCategoryId && finalCategoryId !== '') {
        try {
          setIsLoadingFields(true);
          const response = await productFieldConfigApi.getForProductCreation(parseInt(finalCategoryId));
          setDynamicFields(response.data);
        } catch (error: any) {
          console.error('Error fetching dynamic fields:', error);
          setDynamicFields([]);
        } finally {
          setIsLoadingFields(false);
        }
      } else {
        setDynamicFields([]);
      }
    };

    fetchDynamicFields();
  }, [formData.category_id, formData.sub_category_id]);

  // Fetch states when country changes
  useEffect(() => {
    const fetchStates = async () => {
      if (formData.country_id) {
        try {
          setIsLoadingLocations(true);
          // Use getChildren for cascading dropdown
          const response = await locationsApi.getChildren(parseInt(formData.country_id));
          setStates(response.data || []);
          // Reset state, district, and area when country changes
          setFormData(prev => ({ ...prev, state_id: '', district_id: '', area_id: '' }));
          setDistricts([]);
          setAreas([]);
        } catch (error: any) {
          console.error('Error fetching states:', error);
          setStates([]);
        } finally {
          setIsLoadingLocations(false);
        }
      } else {
        setStates([]);
        setDistricts([]);
        setAreas([]);
        setFormData(prev => ({ ...prev, state_id: '', district_id: '', area_id: '' }));
      }
    };
    
    fetchStates();
  }, [formData.country_id]);

  // Fetch cities when state changes
  useEffect(() => {
    const fetchCities = async () => {
      if (formData.state_id) {
        try {
          setIsLoadingLocations(true);
          // Use getChildren for cascading dropdown - states have cities as children
          const response = await locationsApi.getChildren(parseInt(formData.state_id));
          setCities(response.data || []);
          // Reset city, district and area when state changes
          setFormData(prev => ({ ...prev, city_id: '', district_id: '', area_id: '' }));
          setDistricts([]);
          setAreas([]);
        } catch (error: any) {
          console.error('Error fetching cities:', error);
          setCities([]);
        } finally {
          setIsLoadingLocations(false);
        }
      } else {
        setCities([]);
        setDistricts([]);
        setAreas([]);
        setFormData(prev => ({ ...prev, city_id: '', district_id: '', area_id: '' }));
      }
    };
    
    fetchCities();
  }, [formData.state_id]);

  // Fetch districts when city changes
  useEffect(() => {
    const fetchDistricts = async () => {
      if (formData.city_id) {
        try {
          setIsLoadingLocations(true);
          // Use getChildren for cascading dropdown - cities have districts as children
          const response = await locationsApi.getChildren(parseInt(formData.city_id));
          setDistricts(response.data || []);
          // Reset district and area when city changes
          setFormData(prev => ({ ...prev, district_id: '', area_id: '' }));
          setAreas([]);
        } catch (error: any) {
          console.error('Error fetching districts:', error);
          setDistricts([]);
        } finally {
          setIsLoadingLocations(false);
        }
      } else {
        setDistricts([]);
        setAreas([]);
        setFormData(prev => ({ ...prev, district_id: '', area_id: '' }));
      }
    };
    
    fetchDistricts();
  }, [formData.city_id]);

  // Fetch areas when district changes
  useEffect(() => {
    const fetchAreas = async () => {
      if (formData.district_id) {
        try {
          setIsLoadingLocations(true);
          // Use getChildren for cascading dropdown
          const districtId = parseInt(formData.district_id);
          console.log('Fetching areas for district_id:', districtId);
          const response = await locationsApi.getChildren(districtId);
          console.log('Areas response:', response.data);
          const areasData = response.data || [];
          console.log('Setting areas:', areasData.map((a: any) => `${a.name} (id: ${a.id}, type: ${a.location_type})`));
          setAreas(areasData);
          // Reset area when district changes
          setFormData(prev => ({ ...prev, area_id: '' }));
        } catch (error: any) {
          console.error('Error fetching areas:', error);
          console.error('Error details:', error.response?.data);
          setAreas([]);
        } finally {
          setIsLoadingLocations(false);
        }
      } else {
        setAreas([]);
        setFormData(prev => ({ ...prev, area_id: '' }));
      }
    };
    
    fetchAreas();
  }, [formData.district_id]);

  // Debug: Log categories when they change
  useEffect(() => {
    console.log('Categories in AddProductPage:', categories);
  }, [categories]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    
    if (files.length + images.length > 10) {
      setError('You can only upload up to 10 images');
      return;
    }

    setImages(prev => [...prev, ...files]);

    // Create previews
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews(prev => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemoveImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  // Delivery Options Handlers
  const handleAddDeliveryOption = () => {
    setEditingDeliveryOption(null);
    setIsDeliveryFormOpen(true);
  };

  const handleEditDeliveryOption = (option: DeliveryOption) => {
    setEditingDeliveryOption(option);
    setIsDeliveryFormOpen(true);
  };

  const handleDeleteDeliveryOption = async (option: DeliveryOption) => {
    if (deliveryOptions.length === 1) {
      toast.error('You must have at least one delivery option');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete the delivery option "${option.zone_name}"?`)) {
      return;
    }

    // If editing existing product, delete from server
    if (isEditMode && editingProductId && option.id) {
      try {
        await deliveryOptionApi.deleteDeliveryOption(editingProductId, option.id);
        setDeliveryOptions(prev => prev.filter(opt => opt.id !== option.id));
        toast.success('Delivery option deleted');
      } catch (error: any) {
        const errorMessage = error?.detail || error?.message || 'Failed to delete delivery option';
        toast.error(errorMessage);
      }
    } else {
      // For new products, just remove from local state
      setDeliveryOptions(prev => prev.filter(opt => opt !== option));
      toast.success('Delivery option removed');
    }
  };

  const handleSaveDeliveryOption = async (data: DeliveryOptionCreate) => {
    // If editing existing product, save to server immediately
    if (isEditMode && editingProductId) {
      try {
        if (editingDeliveryOption && editingDeliveryOption.id) {
          // Update existing
          const response = await deliveryOptionApi.updateDeliveryOption(
            editingProductId,
            editingDeliveryOption.id,
            data
          );
          setDeliveryOptions(prev =>
            prev.map(opt => (opt.id === editingDeliveryOption.id ? response.data : opt))
          );
        } else {
          // Create new
          const response = await deliveryOptionApi.createProductDeliveryOption(editingProductId, data);
          setDeliveryOptions(prev => [...prev, response.data]);
        }
      } catch (error: any) {
        throw error; // Let the form handle the error display
      }
    } else {
      // For new products, just add to local state (will be created when product is created)
      if (editingDeliveryOption) {
        setDeliveryOptions(prev =>
          prev.map(opt => (opt === editingDeliveryOption ? { ...opt, ...data } : opt))
        );
      } else {
        setDeliveryOptions(prev => [...prev, { ...data, is_active: true, display_order: prev.length }]);
      }
    }

    setDeliveryValidationError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!shopId) {
      setError('Shop not found. Please create a shop first.');
      return;
    }

    // Validate delivery options (required for all products)
    if (deliveryOptions.length === 0) {
      setDeliveryValidationError('At least one delivery option is required');
      setError('Please add at least one delivery option before saving the product');
      // Scroll to delivery options section
      document.getElementById('delivery-options-section')?.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setDeliveryValidationError('');

    try{
      // Use sub_category_id if selected, otherwise use category_id
      const finalCategoryId = formData.sub_category_id || formData.category_id;
      // Location: Use area_id if selected, otherwise district_id, otherwise city_id, otherwise state_id
      const locationId = formData.area_id || formData.district_id || formData.city_id || formData.state_id;

      // Prepare product data (WITHOUT images)
      // Convert tags from comma-separated string to array
      const tagsArray = formData.tags 
        ? formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0)
        : [];
      
      // Prepare delivery options for API
      const deliveryOptionsForApi = deliveryOptions.map(option => ({
        zone_name: option.zone_name,
        delivery_charge: option.delivery_charge || 0,
        is_free_delivery: option.is_free_delivery || false,
        display_order: option.display_order || 0,
        is_active: true,
      }));

      const productData = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        original_price: formData.original_price ? parseFloat(formData.original_price) : parseFloat(formData.price),
        product_type: formData.product_type,
        category_id: parseInt(finalCategoryId),
        shop_id: shopId,
        brand: formData.brand || null,
        model: formData.model || null,
        condition: formData.condition,
        warranty: formData.warranty || null,
        is_public: formData.is_public,
        tags: tagsArray.length > 0 ? tagsArray : null,
        search_keywords: formData.search_keywords || null,
        location_id: locationId ? parseInt(locationId) : null,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        delivery_options: deliveryOptionsForApi, // Include delivery options
      };

      let productId: number;

      if (isEditMode && editingProductId) {
        // Update existing product
        // Ensure required fields are present
        if (!formData.name || !formData.price || !formData.product_type || !formData.category_id) {
          throw new Error('Required fields are missing');
        }

        // Create FormData for compatibility with existing endpoint
        const updateData = new FormData();
        updateData.append('name', productData.name);
        updateData.append('description', productData.description || '');
        updateData.append('price', productData.price.toString());
        updateData.append('original_price', productData.original_price.toString());
        updateData.append('product_type', productData.product_type);
        updateData.append('category_id', productData.category_id.toString());
        updateData.append('brand', productData.brand || '');
        updateData.append('model', productData.model || '');
        updateData.append('condition', productData.condition);
        updateData.append('warranty', productData.warranty || '');
        updateData.append('is_public', productData.is_public.toString());
        updateData.append('tags', formData.tags); // Send as comma-separated string for FormData
        updateData.append('search_keywords', productData.search_keywords || '');
        if (productData.location_id) updateData.append('location_id', productData.location_id.toString());
        if (productData.latitude) updateData.append('latitude', productData.latitude.toString());
        if (productData.longitude) updateData.append('longitude', productData.longitude.toString());

        await productsApi.update(editingProductId, updateData);
        productId = editingProductId;

        // Note: Delivery options are set during product creation
        // Updating existing delivery options is not fully supported yet
        // Sellers should create products with correct delivery options
        if (deliveryOptions.length > 0) {
          console.log('Delivery options are configured. Update functionality coming soon.');
        }

        // Upload images separately if any
        if (images.length > 0) {
          try {
            // Upload each image (set first as primary)
            for (let i = 0; i < images.length; i++) {
              await imagesApi.upload(productId, [images[i]], i === 0, i);
            }
            toast.success(`Product updated successfully with ${images.length} image(s)!`);
          } catch (imgError: any) {
            console.error('Error uploading images:', imgError);
            toast.error('Product updated but some images failed to upload');
          }
        } else {
          toast.success('Product updated successfully!');
        }
      } else {
        // Create new product with delivery options included
        const response = await productsApi.create(productData);
        productId = response.data.id;

        // Upload images separately if any
        if (images.length > 0) {
          try {
            // Upload each image (set first as primary)
            for (let i = 0; i < images.length; i++) {
              await imagesApi.upload(productId, [images[i]], i === 0, i);
            }
            toast.success(`Product created successfully with ${images.length} image(s)!`);
          } catch (imgError: any) {
            console.error('Error uploading images:', imgError);
            toast.error('Product created but some images failed to upload');
          }
        } else {
          toast.success('Product created successfully!');
        }
      }
      
      navigate('/dashboard/seller/products');
    } catch (error: any) {
      console.error('Error with product:', error);
      setError(error.detail || error.message || 'Failed to save product. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/seller/products')}
          className="mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Products
        </Button>
        <h1 className="text-3xl font-bold text-gray-900">
          {isEditMode ? 'Edit Product' : 'Add New Product'}
        </h1>
        <p className="mt-2 text-gray-600">
          {isEditMode ? 'Update the product details' : 'Fill in the details to create a new product listing'}
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
          
          <div className="space-y-4">
            <Input
              label="Product Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              placeholder="e.g., iPhone 13 Pro Max"
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                required
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Describe your product in detail..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Price"
                name="price"
                type="number"
                step="0.01"
                value={formData.price}
                onChange={handleInputChange}
                required
                placeholder="99.99"
              />

              <Input
                label="Original Price (Optional)"
                name="original_price"
                type="number"
                step="0.01"
                value={formData.original_price}
                onChange={handleInputChange}
                placeholder="149.99"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Product Type
                </label>
                <select
                  name="product_type"
                  value={formData.product_type}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="brand_new">Brand New</option>
                  <option value="recycled">Recycled</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category *
                </label>
                <select
                  name="category_id"
                  value={formData.category_id}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">
                    {categories && categories.length > 0 ? 'Select a category' : 'Loading categories...'}
                  </option>
                  {categories && categories.length > 0 ? (
                    categories
                      .filter((cat: any) => !cat.parent_id) // Only show main categories
                      .map((category: any) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))
                  ) : (
                    <option value="" disabled>No categories available</option>
                  )}
                </select>
                
                {/* Sub-Category - Show when category is selected */}
                {formData.category_id && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sub-Category
                    </label>
                    <select
                      name="sub_category_id"
                      value={formData.sub_category_id}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">Select a sub-category (optional)</option>
                      {subCategories.map((subCat: any) => (
                        <option key={subCat.id} value={subCat.id}>
                          {subCat.name}
                        </option>
                      ))}
                    </select>
                    {subCategories.length === 0 && formData.category_id && (
                      <p className="mt-1 text-xs text-gray-500">No sub-categories available for this category</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Product Details - Dynamic Fields */}
        {formData.category_id && (
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Product Details</h2>

            {isLoadingFields ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                <span className="ml-2 text-gray-600">Loading product details...</span>
              </div>
            ) : dynamicFields.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No product details fields available for this category.</p>
                <p className="text-sm mt-2">
                  Please select a category above to see relevant fields.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {dynamicFields.map((field) => (
                    <div key={field.id} className={field.field_type === 'textarea' ? 'md:col-span-2' : ''}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {field.field_label}
                        {field.is_required && <span className="text-red-500 ml-1">*</span>}
                      </label>

                      {field.field_type === 'text' && (
                        <Input
                          type="text"
                          name={field.field_name}
                          value={formData[field.field_name] || ''}
                          onChange={handleInputChange}
                          required={field.is_required}
                          placeholder={field.placeholder || ''}
                        />
                      )}

                      {field.field_type === 'number' && (
                        <Input
                          type="number"
                          name={field.field_name}
                          value={formData[field.field_name] || ''}
                          onChange={handleInputChange}
                          required={field.is_required}
                          placeholder={field.placeholder || ''}
                          step={field.validation_rules?.step || 'any'}
                          min={field.validation_rules?.min}
                        />
                      )}

                      {field.field_type === 'textarea' && (
                        <textarea
                          name={field.field_name}
                          value={formData[field.field_name] || ''}
                          onChange={handleInputChange}
                          required={field.is_required}
                          rows={4}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder={field.placeholder || ''}
                        />
                      )}

                      {field.field_type === 'select' && (
                        <select
                          name={field.field_name}
                          value={formData[field.field_name] || ''}
                          onChange={handleInputChange}
                          required={field.is_required}
                          disabled={field.field_name === 'brand' ? isLoadingBrands : false}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
                        >
                          <option value="">
                            {field.field_name === 'brand' && isLoadingBrands
                              ? 'Loading brands...'
                              : field.field_name === 'condition'
                              ? 'Select Condition'
                              : field.placeholder || `Select ${field.field_label.toLowerCase()}`}
                          </option>

                          {/* Special handling for brand field */}
                          {field.field_name === 'brand' && brands.map((brand) => (
                            <option key={brand.id} value={brand.name}>
                              {brand.name}
                            </option>
                          ))}

                          {/* Special handling for condition field */}
                          {field.field_name === 'condition' && PRODUCT_CONDITIONS.map((condition) => (
                            <option key={condition} value={condition}>
                              {condition}
                            </option>
                          ))}

                          {/* Default handling for other select fields */}
                          {field.field_name !== 'brand' && field.field_name !== 'condition' && field.options && field.options.map((option: any, index: number) => (
                            <option key={index} value={option.value || option.label}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      )}

                      {/* Show help text for brand field */}
                      {field.field_name === 'brand' && brands.length === 0 && !isLoadingBrands && (
                        <p className="mt-1 text-xs text-gray-500">
                          No brands available. Please contact admin to add brands.
                        </p>
                      )}

                      {field.field_type === 'checkbox' && (
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            name={field.field_name}
                            checked={formData[field.field_name] || false}
                            onChange={handleInputChange}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          />
                          <label className="ml-2 block text-sm text-gray-700">
                            {field.field_label}
                          </label>
                        </div>
                      )}

                      {field.field_type === 'date' && (
                        <Input
                          type="date"
                          name={field.field_name}
                          value={formData[field.field_name] || ''}
                          onChange={handleInputChange}
                          required={field.is_required}
                        />
                      )}

                      {field.help_text && (
                        <p className="mt-1 text-xs text-gray-500">{field.help_text}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Location */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Location</h2>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Country *
                </label>
                <select
                  name="country_id"
                  value={formData.country_id}
                  onChange={handleInputChange}
                  required
                  disabled={isLoadingLocations}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
                >
                  <option value="">
                    {isLoadingLocations ? 'Loading...' : 'Select a country'}
                  </option>
                  {countries.map((country) => (
                    <option key={country.id} value={country.id}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  State/Province *
                </label>
                <select
                  name="state_id"
                  value={formData.state_id}
                  onChange={handleInputChange}
                  required
                  disabled={!formData.country_id || isLoadingLocations}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
                >
                  <option value="">
                    {!formData.country_id ? 'Select country first' : isLoadingLocations ? 'Loading...' : 'Select a state/province'}
                  </option>
                  {states.map((state) => (
                    <option key={state.id} value={state.id}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  City
                </label>
                <select
                  name="city_id"
                  value={formData.city_id}
                  onChange={handleInputChange}
                  disabled={!formData.state_id || isLoadingLocations}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
                >
                  <option value="">
                    {!formData.state_id ? 'Select state first' : isLoadingLocations ? 'Loading...' : 'Select a city (optional)'}
                  </option>
                  {cities.map((city) => (
                    <option key={city.id} value={city.id}>
                      {city.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  District
                </label>
                <select
                  name="district_id"
                  value={formData.district_id}
                  onChange={handleInputChange}
                  disabled={!formData.city_id || isLoadingLocations}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
                >
                  <option value="">
                    {!formData.city_id ? 'Select city first' : isLoadingLocations ? 'Loading...' : 'Select a district (optional)'}
                  </option>
                  {districts.map((district) => (
                    <option key={district.id} value={district.id}>
                      {district.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Area
                </label>
                <select
                  name="area_id"
                  value={formData.area_id}
                  onChange={handleInputChange}
                  disabled={!formData.district_id || isLoadingLocations}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
                >
                  <option value="">
                    {!formData.district_id ? 'Select district first' : isLoadingLocations ? 'Loading...' : 'Select an area (optional)'}
                  </option>
                  {areas.map((area) => (
                    <option key={area.id} value={area.id}>
                      {area.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Map/Coordinates Section */}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  Map Location / GPS Coordinates (Optional)
                </label>
                <button
                  type="button"
                  onClick={() => setShowMapPicker(!showMapPicker)}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  {showMapPicker ? 'Hide Map' : 'Show Map'}
                </button>
              </div>
              
              {showMapPicker && (
                <div className="space-y-3">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                    <p className="font-medium mb-1">How to use:</p>
                    <p>Click on the map below to set your product location, or manually enter latitude/longitude coordinates.</p>
                  </div>
                  
                  {/* Leaflet Map Integration */}
                  <div className="w-full h-64 border border-gray-300 rounded-lg overflow-hidden relative">
                    {showMapPicker ? (
                      <MapContainer
                        center={formData.latitude && formData.longitude 
                          ? [parseFloat(formData.latitude), parseFloat(formData.longitude)]
                          : [23.8103, 90.4125]} // Default to Dhaka, Bangladesh
                        zoom={13}
                        style={{ height: '100%', width: '100%', zIndex: 0 }}
                        scrollWheelZoom={true}
                      >
                        <TileLayer
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        />
                        {formData.latitude && formData.longitude && (
                          <Marker
                            position={[parseFloat(formData.latitude), parseFloat(formData.longitude)]}
                          />
                        )}
                        <LocationPicker
                          onLocationSelect={(lat, lng) => {
                            setFormData(prev => ({
                              ...prev,
                              latitude: lat.toString(),
                              longitude: lng.toString()
                            }));
                            toast.success('Location selected on map!');
                          }}
                        />
                      </MapContainer>
                    ) : (
                      <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                        <div className="text-center">
                          <p className="text-gray-500 mb-2">Map is hidden</p>
                          <p className="text-xs text-gray-400">
                            Click "Show Map" to enable map picker
                          </p>
                        </div>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        // Get user's current location
                        if (navigator.geolocation) {
                          // Clear any previous errors
                          setError(null);

                          navigator.geolocation.getCurrentPosition(
                            (position) => {
                              setFormData(prev => ({
                                ...prev,
                                latitude: position.coords.latitude.toString(),
                                longitude: position.coords.longitude.toString()
                              }));
                              toast.success('Location captured successfully!');
                            },
                            (error) => {
                              console.error('Geolocation error:', error);

                              let errorMessage = 'Could not get your location. ';

                              switch(error.code) {
                                case error.PERMISSION_DENIED:
                                  errorMessage += 'Location access was denied. Please allow location access in your browser settings and try again.';
                                  break;
                                case error.POSITION_UNAVAILABLE:
                                  errorMessage += 'Location information is unavailable. Please check your device settings and try again.';
                                  break;
                                case error.TIMEOUT:
                                  errorMessage += 'Location request timed out. Please try again or enter coordinates manually.';
                                  break;
                                default:
                                  errorMessage += 'Please enter coordinates manually or click on the map to set location.';
                                  break;
                              }

                              setError(errorMessage);
                            },
                            {
                              enableHighAccuracy: true,
                              timeout: 10000, // 10 seconds
                              maximumAge: 300000 // Accept cached location up to 5 minutes old
                            }
                          );
                        } else {
                          setError('Geolocation is not supported by this browser. Please enter coordinates manually or click on the map.');
                        }
                      }}
                      className="absolute bottom-2 right-2 px-3 py-1 bg-primary-600 text-white rounded text-sm hover:bg-primary-700 z-10"
                    >
                      Use My Location
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Latitude"
                      name="latitude"
                      type="number"
                      step="any"
                      value={formData.latitude}
                      onChange={handleInputChange}
                      placeholder="e.g., 23.8103"
                    />
                    <Input
                      label="Longitude"
                      name="longitude"
                      type="number"
                      step="any"
                      value={formData.longitude}
                      onChange={handleInputChange}
                      placeholder="e.g., 90.4125"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Product Images */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Product Images</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Images (Max 10)
              </label>
              <div className="flex items-center justify-center w-full">
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <PhotoIcon className="w-10 h-10 mb-3 text-gray-400" />
                    <p className="mb-2 text-sm text-gray-500">
                      <span className="font-semibold">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-gray-500">PNG, JPG, WEBP (MAX. 5MB)</p>
                  </div>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </label>
              </div>
            </div>

            {/* Image Previews */}
            {imagePreviews.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {imagePreviews.map((preview, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={preview}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-32 object-cover rounded-lg"
                      onError={(e) => {
                        console.warn('Image preview failed to load for index', index);
                        // Remove the failed preview
                        setImagePreviews(prev => prev.filter((_, i) => i !== index));
                        setImages(prev => prev.filter((_, i) => i !== index));
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(index)}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Delivery Options Section */}
        <div id="delivery-options-section" className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Delivery Options <span className="text-red-500">*</span>
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Configure delivery zones and charges for this product
              </p>
            </div>
            <Button
              type="button"
              onClick={handleAddDeliveryOption}
              disabled={isSubmitting}
              className="flex items-center"
            >
              <TruckIcon className="h-5 w-5 mr-2" />
              Add Zone
            </Button>
          </div>

          {deliveryValidationError && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {deliveryValidationError}
            </div>
          )}

          {deliveryOptions.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <TruckIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No delivery options</h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by adding your first delivery zone
              </p>
              <div className="mt-4">
                <Button
                  type="button"
                  onClick={handleAddDeliveryOption}
                  disabled={isSubmitting}
                >
                  <TruckIcon className="h-5 w-5 mr-2" />
                  Add Delivery Zone
                </Button>
              </div>
              <p className="mt-3 text-xs text-gray-500">
                Note: At least one delivery zone is required
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {deliveryOptions.map((option, index) => (
                <div
                  key={option.id || index}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-primary-300 transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center">
                      <h3 className="text-sm font-medium text-gray-900">{option.zone_name}</h3>
                      {option.is_free_delivery ? (
                        <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          FREE
                        </span>
                      ) : (
                        <span className="ml-3 text-sm font-semibold text-primary-600">
                          {option.delivery_charge} tk
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Display order: {option.display_order || 0}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      type="button"
                      onClick={() => handleEditDeliveryOption(option)}
                      disabled={isSubmitting}
                      className="p-2 text-gray-400 hover:text-primary-600 transition-colors disabled:opacity-50"
                      title="Edit"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteDeliveryOption(option)}
                      disabled={isSubmitting || deliveryOptions.length === 1}
                      className="p-2 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title={deliveryOptions.length === 1 ? 'Cannot delete last option' : 'Delete'}
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              ))}

              <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-800">
                  <strong>Tip:</strong> Add multiple delivery zones to give buyers options. For example, "Within Dhaka: 60 tk" and "Outside Dhaka: 120 tk".
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Visibility */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Visibility</h2>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              name="is_public"
              checked={formData.is_public}
              onChange={handleInputChange}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-700">
              Make product visible to public
            </label>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/dashboard/seller/products')}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? (isEditMode ? 'Updating Product...' : 'Creating Product...')
              : (isEditMode ? 'Update Product' : 'Create Product')
            }
          </Button>
        </div>
      </form>

      {/* Delivery Option Form Modal */}
      <DeliveryOptionForm
        isOpen={isDeliveryFormOpen}
        onClose={() => {
          setIsDeliveryFormOpen(false);
          setEditingDeliveryOption(null);
        }}
        onSave={handleSaveDeliveryOption}
        editingOption={editingDeliveryOption}
        title={editingDeliveryOption ? 'Edit Delivery Zone' : 'Add Delivery Zone'}
      />
    </div>
  );
};

export default AddProductPage;

